import React from 'react';
import { arrayOf, bool, func, node, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';
import IconCollection from '../../../../components/IconCollection/IconCollection';
import SectionContainer from '../SectionContainer';
import css from './SectionColumns.module.css';

import exploreByRegionMap from '../../../../assets/regionMap.png';
import northAmericaMap from '../../../../assets/northamerica-copy.png';
import centralAmericaMap from '../../../../assets/centralamerica.png';
import southAmericaMap from '../../../../assets/southamerica-copy.png';
import europeMap from '../../../../assets/Europe.png';
import middleEastMap from '../../../../assets/Middleeast-copy.png';
import africaMap from '../../../../assets/Africa-copy.png';
import caribbeanMap from '../../../../assets/Carribean.png';
import centralAsiaMap from '../../../../assets/CentralNorthAsia.png';
import oceaniaMap from '../../../../assets/Oceania-copy.png';
import southEastAsiaMap from '../../../../assets/SouthEastAsia-copy.png';

import { NamedLink } from '../../../../components';
import { stringToKey } from '../../../../util/data';

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 265px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

const Accordion = ({ title, children, index, isOpen, toggleOpen, pageId }) => {
  const isMobileLayout = typeof window != "undefined" && window?.innerWidth <= 768;
  return (
    <div className={css.accordion}>
      <div className={css.accordionHeader} onClick={() => !isMobileLayout && toggleOpen(index)} >
        {isMobileLayout ? <NamedLink
          name="CMSPage"
          params={{ pageId: pageId }}
        >
          {`${index + 1}.`}&nbsp; {title}{' '}
        </NamedLink> :
          <>{`${index + 1}.`}&nbsp; {title}{' '}</>
        }
        <span className={css.accordionIcon} onClick={() => isMobileLayout && toggleOpen(index)}>
          {isOpen ? <IconCollection icon="iconMinus" /> : <IconCollection icon="iconPlus" />}
        </span>
      </div>
      {isOpen && <div className={css.accordionContent}>{children}</div>}
    </div>
  );
};

// Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
const SectionColumns = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks,
    isInsideContainer,
    options,
  } = props;

  const [openAccordion, setOpenAccordion] = React.useState(null);

  const toggleOpen = index => {
    setOpenAccordion(prevState => (prevState === index ? null : index));
  };

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  const regionsData = [
    {
      id: 1,
      title: "Africa",
      key: "africa",
      image: africaMap,
      countries: [
        // { key: "algeria", label: "Algeria" },
        // { key: "angola", label: "Angola" },
        // { key: "benin", label: "Benin" },
        // { key: "botswana", label: "Botswana" },
        // { key: "burkina-faso", label: "Burkina Faso" },
        { key: "egypt", label: "Egypt" },
        { key: "kenya", label: "Kenya" },
        { key: "morocco", label: "Morocco" },
        { key: "namibia", label: "Namibia" },
        { key: "south-africa", label: "South Africa" },
        { key: "tanzania", label: "Tanzania" },
      ],
    },
    {
      id: 2,
      title: "Caribbean",
      key: "carribean",
      image: caribbeanMap,
      countries: [
        // { key: "antigua-and-barbuda", label: "Antigua and Barbuda" },
        { key: "bahamas", label: "Bahamas" },
        { key: "barbados", label: "Barbados" },
        { key: "cuba", label: "Cuba" },
        // { key: "jamaica", label: "Jamaica" },
      ],
    },
    {
      id: 3,
      title: "Central America",
      key: "central-america",
      image: centralAmericaMap,
      countries: [
        { key: "costa-rica", label: "Costa Rica" },
        // { key: "el-salvador", label: "El Salvador" },
        { key: "guatemala", label: "Guatemala" },
        // { key: "honduras", label: "Honduras" },
        { key: "nicaragua", label: "Nicaragua" },
        { key: "panama", label: "Panama" },
      ],
    },
    {
      id: 4,
      title: "Central & North Asia",
      key: "central-and-north-asia",
      image: centralAsiaMap,
      countries: [
        // { key: "kazakhstan", label: "Kazakhstan" },
        // { key: "kyrgyzstan", label: "Kyrgyzstan" },
        { key: "mongolia", label: "Mongolia" },
        // { key: "tajikistan", label: "Tajikistan" },
        // { key: "turkmenistan", label: "Turkmenistan" },
        // { key: "uzbekistan", label: "Uzbekistan" },
      ],
    },
    {
      id: 5,
      title: "Europe",
      key: "europe",
      image: europeMap,
      countries: [
        // { key: "albania", label: "Albania" },
        // { key: "andorra", label: "Andorra" },
        { key: "austria", label: "Austria" },
        // { key: "azerbaijan", label: "Azerbaijan" },
        // { key: "belarus", label: "Belarus" },
        // { key: "belgium", label: "Belgium" },
        // { key: "bosnia-and-herzegovina", label: "Bosnia and Herzegovina" },
        // { key: "bulgaria", label: "Bulgaria" },
        { key: "croatia", label: "Croatia" },
        { key: "denmark", label: "Denmark" },
        { key: "finland", label: "Finland" },
        { key: "france", label: "France" },
        { key: "germany", label: "Germany" },
        { key: "greece", label: "Greece" },
        { key: "iceland", label: "Iceland" },
        { key: "ireland", label: "Ireland" },
        { key: "italy", label: "Italy" },
        { key: "netherlands", label: "Netherlands" },
        { key: "norway", label: "Norway" },
        { key: "portugal", label: "Portugal" },
        { key: "spain", label: "Spain" },
        { key: "sweden", label: "Sweden" },
        { key: "switzerland", label: "Switzerland" },
      ],
    },
    {
      id: 6,
      title: "Middle East",
      image: middleEastMap,
      key: "middle-east",
      countries: [
        // { key: "bahrain", label: "Bahrain" },
        // { key: "iran", label: "Iran" },
        // { key: "iraq", label: "Iraq" },
        // { key: "israel", label: "Israel" },
        { key: "united-arab-emirates", label: "United Arab Emirates" },
        { key: "jordan", label: "Jordan" },
        { key: "oman", label: "Oman" },

      ],
    },
    {
      id: 7,
      title: "North America",
      key: "north-america",
      image: northAmericaMap,
      countries: [
        { key: "canada", label: "Canada" },
        { key: "mexico", label: "Mexico" },
        { key: "greenland", label: "Greenland" },
        { key: "united-states", label: "United States" }
      ],
    },
    {
      id: 8,
      title: "Oceania",
      key: "oceania",
      image: oceaniaMap,
      countries: [
        { key: "australia", label: "Australia" },
        { key: "fiji", label: "Fiji" },
        { key: "new-zealand", label: "New Zealand" },
        { key: "french-polynesia", label: "French Polynesia" },
        // { key: "papua_new_guinea", label: "Papua New Guinea" },
        // { key: "samoa", label: "Samoa" },
      ],
    },
    {
      id: 9,
      title: "South America",
      key: "south-america",
      image: southAmericaMap,
      countries: [
        { key: "argentina", label: "Argentina" },
        { key: "bolivia", label: "Bolivia" },
        { key: "brazil", label: "Brazil" },
        { key: "chile", label: "Chile" },
        { key: "colombia", label: "Colombia" },
        { key: "ecuador", label: "Ecuador" },
        { key: "peru", label: "Peru" },
      ],
    },
    {
      id: 10,
      title: "South & East Asia",
      key: "south-and-east-asia",
      image: southEastAsiaMap,
      countries: [
        { key: "japan", label: "Japan" },
        { key: "indonesia", label: "Indonesia" },
        { key: "malaysia", label: "Malaysia" },
        { key: "philippines", label: "Philippines" },
        { key: "thailand", label: "Thailand" },
        { key: "vietnam", label: "Vietnam" },
        { key: "india", label: "India" },
        { key: "the_maldives", label: "Maldives" }
      ],
    },
  ];

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {sectionId == 'explore-by-region' ? (
        <div className={css.exploreByRegionContainer}>
          <div className={css.exploreByRegionContent}>
            <header className={defaultClasses.sectionDetails}>
              <Field data={title} className={defaultClasses.title} options={fieldOptions} />
              <Field
                data={description}
                className={defaultClasses.description}
                options={fieldOptions}
              />
              <Field
                data={callToAction}
                className={defaultClasses.ctaButton}
                options={fieldOptions}
              />
            </header>
            <div id="accordions" className={css.accordionContainer}>
              {regionsData.map((region, index) => (
                <Accordion
                  key={index}
                  title={region.title}
                  index={index}
                  isOpen={openAccordion === index}
                  toggleOpen={toggleOpen}
                  pageId={region.key}
                >
                  <ul>
                    {region.countries.map((country, idx) => (
                      <li key={country.key}>
                        <NamedLink
                          name="CMSPage"
                          params={{ pageId: country.key }}
                        >
                          {country.label}
                        </NamedLink>
                      </li>
                    ))}
                  </ul>
                </Accordion>
              ))}
            </div>
            <div></div>
          </div>
          <div className={css.exploreByRegionMap}>
            <img src={exploreByRegionMap} alt="Explore by region map" />
            <div>
              {regionsData.map(({ id, title, image, key }) => <div key={id} className={css.mapItem}>
                <NamedLink name="CMSPage" params={{ pageId: key || stringToKey(title) || "1111" }}>
                  <div>
                    <span>{id}</span>
                    <img src={image} alt={title} />
                  </div>
                  <p>{title}</p>
                </NamedLink>
              </div>)}
            </div>
          </div>
        </div>
      ) : (
        <>
          {hasHeaderFields ? (
            <header className={defaultClasses.sectionDetails}>
              <Field data={title} className={defaultClasses.title} options={fieldOptions} />
              <Field
                data={description}
                className={defaultClasses.description}
                options={fieldOptions}
              />
              <Field
                data={callToAction}
                className={defaultClasses.ctaButton}
                options={fieldOptions}
              />
            </header>
          ) : null}
          {hasBlocks ? (
            <div
              className={classNames(defaultClasses.blockContainer, getColumnCSS(numColumns), {
                [css.noSidePaddings]: isInsideContainer,
              })}
            >
              <BlockBuilder
                ctaButtonClass={defaultClasses.ctaButton}
                blocks={blocks}
                sectionId={sectionId}
                responsiveImageSizes={getResponsiveImageSizes(numColumns)}
                options={options}
              />
            </div>
          ) : null}
        </>
      )}
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionColumns.defaultProps = {
  className: null,
  rootClassName: null,
  defaultClasses: null,
  textClassName: null,
  numColumns: 1,
  title: null,
  description: null,
  appearance: null,
  callToAction: null,
  blocks: [],
  isInsideContainer: false,
  options: null,
};

SectionColumns.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  defaultClasses: shape({
    sectionDetails: string,
    title: string,
    description: string,
    ctaButton: string,
  }),
  numColumns: number,
  title: object,
  description: object,
  appearance: object,
  callToAction: object,
  blocks: arrayOf(object),
  isInsideContainer: bool,
  options: propTypeOption,
};

export default SectionColumns;
